import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import * as ambassadorWixAtlasV2Prediction from './atlas-v2-prediction-autocomplete.http.js';

export interface Prediction {
  /** The human-readable name of the prediction */
  description?: string;
  /** The id of the prediction that can be use in place api. Available for short time. */
  searchId?: string;
  /** Contains an array with offset value and length. These describe the location of the entered term in the prediction result text, so that the term can be highlighted if desired */
  matchedSubstrings?: MatchedSubstrings[];
  /** Provides pre-formatted text that can be shown in your autocomplete results */
  textStructure?: TextStructure;
  /** Contains an integer indicating the straight-line distance between the predicted place, and the specified origin point, in meters. */
  distanceInMeters?: number | null;
}

export interface MatchedSubstrings {
  length?: number;
  offset?: number;
}

export interface TextStructure {
  /** Contains the main text of a prediction, usually the name of the place */
  mainText?: string;
  /** Contains the secondary text of a prediction, usually the location of the place */
  secondaryText?: string;
  /** Contains an array with offset value and length. These describe the location of the entered term in the prediction result text, so that the term can be highlighted if desired */
  mainTextMatchedSubstrings?: MatchedSubstrings[];
}

export interface ListPredictionsRequest {
  /** The text the predictions will be based on. */
  input?: string;
  /** The origin point from which to calculate straight-line distance to the destination */
  origin?: AddressLocation;
  /** The point around which you wish to retrieve place information */
  location?: AddressLocation;
  /** The acceptable distance from that location (in meters) */
  radius?: string | null;
  /** Filters the user can add in order to get more accurate results */
  filterBy?: FilterBy[];
}

export interface AddressLocation {
  /** Address latitude. */
  latitude?: number | null;
  /** Address longitude. */
  longitude?: number | null;
}

export interface FilterBy {
  /** One of the filter types enum */
  filterType?: FilterTypeWithLiterals;
  /** Free text like "us" */
  filterValue?: string;
}

export enum FilterType {
  /** Filter by zip code */
  zip_code = 'zip_code',
  /** Filter by 2-letters or 3-letters country code */
  country_code = 'country_code',
}

/** @enumType */
export type FilterTypeWithLiterals = FilterType | 'zip_code' | 'country_code';

export interface ListPredictionsResponse {
  predictions?: Prediction[];
}

export interface PredictRequest {
  /**
   * The text the predictions will be based on.
   * @minLength 1
   */
  input: string;
  /** The origin point from which to calculate straight-line distance to the destination */
  origin?: AddressLocation;
  /** The point around which you wish to retrieve place information */
  location?: AddressLocation;
  /** The acceptable distance from that location (in meters) */
  radius?: string | null;
  /** alpha-2 or alpha-3 ISO-3166 country codes to filter by */
  countryCodes?: string[];
  /**
   * A random string which identifies an autocomplete session for billing purposes. The session begins when the user starts typing a query, and concludes when they select a place and a call to Get Place is made. Once a session has concluded, the token is no longer valid. your app must generate a fresh token for each session.
   * @format GUID
   */
  sessionToken?: string | null;
}

export interface PredictResponse {
  predictions?: Prediction[];
}

/**
 * A Predict end-point take an input and returns an list of Prediction object.
 * @param input - The text the predictions will be based on.
 * @public
 * @documentationMaturity preview
 * @requiredField input
 * @permissionId WIX_ATLAS.LIST_PREDICTIONS
 * @applicableIdentity APP
 * @fqn com.wixpress.vi.atlas.service.v2.AutocompleteServiceV2.Predict
 */
export async function predict(
  input: string,
  options?: PredictOptions
): Promise<
  NonNullablePaths<
    PredictResponse,
    | `predictions`
    | `predictions.${number}.description`
    | `predictions.${number}.searchId`
    | `predictions.${number}.textStructure.mainText`
    | `predictions.${number}.textStructure.secondaryText`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    input: input,
    origin: options?.origin,
    location: options?.location,
    radius: options?.radius,
    countryCodes: options?.countryCodes,
    sessionToken: options?.sessionToken,
  });

  const reqOpts = ambassadorWixAtlasV2Prediction.predict(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          input: '$[0]',
          origin: '$[1].origin',
          location: '$[1].location',
          radius: '$[1].radius',
          countryCodes: '$[1].countryCodes',
          sessionToken: '$[1].sessionToken',
        },
        singleArgumentUnchanged: false,
      },
      ['input', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface PredictOptions {
  /** The origin point from which to calculate straight-line distance to the destination */
  origin?: AddressLocation;
  /** The point around which you wish to retrieve place information */
  location?: AddressLocation;
  /** The acceptable distance from that location (in meters) */
  radius?: string | null;
  /** alpha-2 or alpha-3 ISO-3166 country codes to filter by */
  countryCodes?: string[];
  /**
   * A random string which identifies an autocomplete session for billing purposes. The session begins when the user starts typing a query, and concludes when they select a place and a call to Get Place is made. Once a session has concluded, the token is no longer valid. your app must generate a fresh token for each session.
   * @format GUID
   */
  sessionToken?: string | null;
}
